// @flow
import React, {useState, useCallback, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {
  Modal,
  Paper,
  Typography,
  Box,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@material-ui/core';

import {Close} from '@material-ui/icons';
import SearchBar from '../SearchBar/SearchBar';
import CustomButton from '../CustomButton/CustomButton';
import {getImageUrl} from '../../utils/image_provider';
import {colors} from '../../assets/styles/colors';

import type {Node} from 'react';
import type {Product, Category, Subcategory, Pagination} from '../../entities';
import {memo} from 'react';

type Props = {|
  maxItems?: number,
  title: string,
  open: boolean,
  handleClose: () => void,
  type?: 'PRODUCT_CATEGORY' | 'PRODUCT' | 'PRODUCT_SUB_CATEGORY',
  items?: Product[] | Category[] | Subcategory[],
  isFetchingItems?: boolean,
  pagination?: Pagination,
  setListItems: (any[]) => void,
  listItems: any[],
  resetListFilter: () => any,
  buildCustomQueryParamsCallback: () => {[key: string]: any},
  customFetchItemsCallback: (
    queryFilter: {[key: string]: any},
    addPage?: boolean
  ) => void,
|};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'grid',
    justifyItems: 'center',
    alignItems: 'center',
  },
  loadingSpinner: {
    alignSelf: 'center',
    marginTop: theme.spacing(3),
  },
  paper: {
    display: 'block',
    width: 600,
    minHeight: 536,
    position: 'relative',
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3),
      width: '100%',
    },
    '&:focus': {
      outline: 'none',
    },
  },
  addButton: {
    width: '100%',
    padding: theme.spacing(1.75, 1),
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
      visibility: 'hidden',
    },
  },
  closeBox: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
  },
  content: {
    width: '100%',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '&[data-disabled="true"]': {
      pointerEvents: 'not-allowed',
      opacity: 0.5,
    },
  },
  nameProduct: {
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    color: colors.grey800,
    margin: 'auto 8px',
  },
  costProduct: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    color: colors.grey1000,
  },
  imageItem: {
    borderRadius: '8px',
  },
  divider: {
    border: '1px solid #E8E8ED',
    height: '1px',
    width: '100%',
  },
  wrapperItem: {
    margin: '16px auto',
  },
  scrollableWrapper: {
    overflowY: 'auto',
    maxHeight: '60vh',
    margin: '16px auto',
    padding: '16px',
  },
  option: {
    width: '100%',
  },
  actionSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 22,
    fontWeight: 600,
    color: '#313135',
  },
}));

export const MAP_ITEMS_TYPE = {
  PRODUCT: 'productos',
  PRODUCT_CATEGORY: 'categorías',
  PRODUCT_SUB_CATEGORY: 'subcategorías',
};

function ModalSelectItems(props: Props) {
  const {
    maxItems,
    title,
    open,
    handleClose,
    type = 'PRODUCT',
    items,
    isFetchingItems,
    pagination,
    setListItems,
    listItems,
    resetListFilter,
    buildCustomQueryParamsCallback,
    customFetchItemsCallback,
  } = props;

  const classes = useStyles();
  const [savedSearchString, setSavedSearchString] = useState('');
  const [localSelectedItems, setLocalSelectedItems] = useState<any[]>([]);

  const buildQueryParamsCallback = useCallback(
    (
      searchString?: string
    ): {
      [key: string]: any,
    } => {
      let params = buildCustomQueryParamsCallback();
      if (type === 'PRODUCT') params.querySize = 'MINIMAL';
      if (searchString) {
        params.name = searchString;
        setSavedSearchString(searchString);
      }
      return params;
    },
    [buildCustomQueryParamsCallback, type]
  );

  useEffect(() => {
    if (open) {
      const queryFilter = buildQueryParamsCallback(savedSearchString);
      customFetchItemsCallback({...queryFilter, page: 0});
    }
  }, [
    buildQueryParamsCallback,
    customFetchItemsCallback,
    open,
    savedSearchString,
  ]);

  useEffect(() => {
    if (listItems) setLocalSelectedItems(listItems);
  }, [listItems]);

  useEffect(() => {
    if (!open) setSavedSearchString('');
    return () => resetListFilter();
  }, [open, resetListFilter]);

  const getDisabledOption = (id: string) => {
    if (!isNaN(maxItems)) {
      if (localSelectedItems.find(p => p.id === id)) return false;
      return localSelectedItems.length >= Number(maxItems);
    } else return false;
  };

  const handleAddItem = (e, index: number) => {
    const posItem: number = localSelectedItems.findIndex(
      i => items && i.id === items[index].id
    );

    const newList = [...localSelectedItems];
    if (e.target.checked) {
      if (posItem === -1 && items)
        newList.push({...items[index], checked: true});
    } else {
      if (posItem > -1) {
        newList.splice(posItem, 1);
      }
    }

    setLocalSelectedItems(newList);
  };

  const listInnerRef = useRef();

  const onScroll = () => {
    if (listInnerRef.current) {
      const {scrollTop, scrollHeight, clientHeight} = listInnerRef.current;
      if (scrollTop + clientHeight === scrollHeight) {
        handleAddPage();
      }
    }
  };

  const arrayListSearched = (): Node => {
    return (
      <div
        className={classes.scrollableWrapper}
        onScroll={onScroll}
        ref={listInnerRef}
      >
        <hr className={classes.divider} />

        {// $FlowFixMe
        items.map((p: any, index: number) => {
          let disabled = getDisabledOption(p.id);

          if (typeof p.enabled === 'boolean') {
            disabled = disabled || !p.enabled;
          }

          return (
            <div key={`${index}-${p.name}`} className={classes.wrapperItem}>
              <FormControlLabel
                classes={{label: classes.item}}
                className={classes.option}
                control={
                  <Checkbox
                    color="primary"
                    disabled={disabled}
                    checked={!!localSelectedItems.find(l => l.id === p.id)}
                    onChange={e => handleAddItem(e, index)}
                  />
                }
                label={
                  type === 'PRODUCT' ? (
                    <div
                      className={classes.item}
                      style={{justifyContent: 'space-between'}}
                      data-disabled={disabled}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          className={classes.imageItem}
                          alt={p.name}
                          src={getImageUrl(p.mainImageId)}
                          width="56"
                          height="56"
                        />
                        <div className={classes.nameProduct}>{p.name}</div>
                      </div>

                      <div className={classes.costProduct}>{p.price}</div>
                    </div>
                  ) : (
                    <div className={classes.item} data-disabled={disabled}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {p.portraitImageId && (
                          <img
                            className={classes.imageItem}
                            alt={p.name}
                            src={getImageUrl(p.portraitImageId)}
                            width="56"
                            height="56"
                          />
                        )}
                        <div className={classes.nameProduct}>{p.name}</div>
                      </div>
                    </div>
                  )
                }
              />
              <hr className={classes.divider} />
            </div>
          );
        })}

        {isFetchingItems && (
          <div
            style={{
              textAlign: 'center',
            }}
          >
            <CircularProgress />
          </div>
        )}
      </div>
    );
  };

  const handleSaveList = () => {
    setListItems(localSelectedItems);
    setLocalSelectedItems([]);
    handleClose();
  };

  const handleAddPage = useCallback(() => {
    if (
      !isFetchingItems &&
      localSelectedItems &&
      pagination &&
      pagination.total > pagination.size &&
      localSelectedItems.length < pagination.total
    ) {
      const queryFilter = buildQueryParamsCallback(savedSearchString);
      customFetchItemsCallback(queryFilter, true);
    }
  }, [
    buildQueryParamsCallback,
    customFetchItemsCallback,
    isFetchingItems,
    localSelectedItems,
    pagination,
    savedSearchString,
  ]);

  const fetchItemsCallback = useCallback(
    (searchString?: string) => {
      if (!searchString) setSavedSearchString('');
      const queryFilter = buildQueryParamsCallback(searchString);
      customFetchItemsCallback(queryFilter);
    },
    [buildQueryParamsCallback, customFetchItemsCallback]
  );

  const typeItems = MAP_ITEMS_TYPE[type];

  return (
    <Modal
      className={classes.root}
      open={open}
      onClose={() => {
        setLocalSelectedItems([]);
        handleClose();
      }}
    >
      <Paper className={classes.paper}>
        <Box className={classes.closeBox}>
          <IconButton aria-label="close" onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
        <Box className={classes.content}>
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            className={classes.title}
          >
            {title}
          </Typography>

          <SearchBar
            mobile={true}
            placeholder={`Buscar ${typeItems}`}
            color="light"
            externalHandleSearch={fetchItemsCallback}
          />
          {items ? (
            <>{arrayListSearched()}</>
          ) : isFetchingItems ? (
            <div style={{width: 'fit-content', margin: 'auto'}}>
              <CircularProgress className={classes.loadingSpinner} />
            </div>
          ) : (
            <div style={{margin: '36px auto'}}>No se encontraron productos</div>
          )}
        </Box>
        <Box className={classes.actionSection}>
          <div
            className={classes.costProduct}
          >{`${localSelectedItems.length} ${typeItems} seleccionados`}</div>
          <CustomButton
            color="primary"
            variant="contained"
            size="large"
            onClick={handleSaveList}
            disabled={localSelectedItems.length === 0}
          >
            Agregar
          </CustomButton>
        </Box>
      </Paper>
    </Modal>
  );
}

ModalSelectItems.propTypes = {
  maxItems: PropTypes.number,
  title: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  type: PropTypes.string,
  items: PropTypes.array,
  isFetchingItems: PropTypes.bool,
  pagination: PropTypes.object,
  setListItems: PropTypes.func.isRequired,
  listItems: PropTypes.array,
  resetListFilter: PropTypes.func,
  customFetchItemsCallback: PropTypes.func.isRequired,
  buildCustomQueryParamsCallback: PropTypes.func.isRequired,
};

export default memo<Props>(ModalSelectItems);
